package app.nestbridge.app.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Key
import androidx.compose.material.icons.outlined.VerifiedUser
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.auth.model.NativeProvider
import com.karawek.core.auth.model.OAuthProvider
import com.karawek.core.auth.model.Passkey
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.screen.AuthenticationEvent
import com.karawek.core.auth.screen.AuthenticationScreen
import com.karawek.core.auth.screen.AuthenticationState
import com.karawek.core.ui.components.CoreChip
import com.karawek.core.ui.components.CoreLoadingIndicator
import com.karawek.core.ui.components.CorePhoneTextField
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSurface
import com.karawek.core.ui.components.CoreTextField
import com.karawek.core.ui.theme.CoreShapes
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import nestbridge.composeapp.generated.resources.Res
import nestbridge.composeapp.generated.resources.ic_apple_logo
import nestbridge.composeapp.generated.resources.ic_google_logo
import nestbridge.composeapp.generated.resources.ic_nestbridge_logo
import org.jetbrains.compose.resources.painterResource
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = AuthenticationScreen::class,
    scope = AppScope::class,
)
@Composable
fun AuthenticationUi(
    state: AuthenticationState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        scrollable = false,
        footer = { Footnotes() },
        onAppear = { state.eventSink(AuthenticationEvent.OnAppear) },
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.lg),
        ) {
            WelcomeMessage(
                modifier = Modifier.weight(1f),
            )

            CoreSurface(
                modifier = Modifier.fillMaxWidth(),
            ) {
                AuthenticationPanel(state)
            }
        }
    }
}

@Composable
private fun WelcomeMessage(
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Image(
            painter = painterResource(Res.drawable.ic_nestbridge_logo),
            contentDescription = "NestBridge logo",
            modifier = Modifier.size(72.dp),
        )

        Text(
            text = "Welcome to NestBridge",
            style = CoreTypography.headline,
            color = CoreTheme.colors.textPrimary,
            modifier = Modifier.padding(top = CoreSpacing.md, bottom = CoreSpacing.md),
        )

        Text(
            text = "A shared space for coordinating care — you'll review any invitations before joining anything.",
            style = CoreTypography.caption,
            color = CoreTheme.colors.textSecondary,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun AuthenticationPanel(
    state: AuthenticationState,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        AuthChannelSelector(
            state = state,
        )

        if (state.selectedOtpChannel == OtpChannel.Phone) {
            CorePhoneTextField(
                value = state.otpDestination,
                onValueChange = {
                    state.eventSink(AuthenticationEvent.UpdateOtpDestination(it))
                },
                label = "Phone number",
                placeholder = "(555) 123-4567",
                modifier = Modifier.fillMaxWidth(),
                error = state.otpErrorMessage,
            )
        } else {
            CoreTextField(
                value = state.otpDestination,
                onValueChange = {
                    state.eventSink(AuthenticationEvent.UpdateOtpDestination(it))
                },
                label = when (state.selectedOtpChannel) {
                    OtpChannel.Email -> "Email address"
                    else -> "Email or phone"
                },
                placeholder = when (state.selectedOtpChannel) {
                    OtpChannel.Email -> "you@example.com"
                    else -> ""
                },
                modifier = Modifier.fillMaxWidth(),
                error = state.otpErrorMessage,
            )
        }

        if (state.isLoading) {
            CoreLoadingIndicator(
                modifier = Modifier
                    .align(Alignment.CenterHorizontally)
                    .padding(vertical = CoreSpacing.sm),
            )
        } else {
            CorePrimaryButton(
                text = "Send verification code",
                onClick = {
                    state.eventSink(AuthenticationEvent.StartOtp)
                },
                enabled = state.otpDestination.isNotBlank(),
                modifier = Modifier.fillMaxWidth(),
            )

            if (state.availableMethods.contains(Passkey)) {
                SocialButton(
                    text = "Sign in with a passkey",
                    background = CoreTheme.colors.surface,
                    contentColor = CoreTheme.colors.textPrimary,
                    border = BorderStroke(1.dp, CoreTheme.colors.divider),
                    onClick = { state.eventSink(AuthenticationEvent.StartPasskey) },
                    leadingContent = {
                        Icon(
                            imageVector = Icons.Outlined.Key,
                            contentDescription = "Passkey",
                            tint = CoreTheme.colors.textPrimary,
                            modifier = Modifier
                                .padding(CoreSpacing.xs)
                                .size(18.dp),
                        )
                    },
                )
            }
        }

        SocialAuthSection(state)

        state.generalErrorMessage?.let {
            Text(
                text = it,
                style = CoreTypography.caption,
                color = CoreTheme.colors.error,
            )
        }
    }
}

@Composable
private fun AuthChannelSelector(
    state: AuthenticationState,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(CoreSpacing.xs),
        horizontalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
    ) {
        val phoneEnabled = state.availableMethods.contains(OtpChannel.Phone)
        CoreChip(
            selected = state.selectedOtpChannel == OtpChannel.Phone,
            enabled = phoneEnabled,
            label = "Phone",
            modifier = Modifier.weight(1f),
            onClick = {
                state.eventSink(AuthenticationEvent.SelectOtpChannel(OtpChannel.Phone))
            },
        )

        val emailEnabled = state.availableMethods.contains(OtpChannel.Email)
        CoreChip(
            selected = state.selectedOtpChannel == OtpChannel.Email,
            enabled = emailEnabled,
            label = "Email",
            modifier = Modifier.weight(1f),
            onClick = {
                state.eventSink(AuthenticationEvent.SelectOtpChannel(OtpChannel.Email))
            },
        )
    }
}

@Composable
private fun SocialAuthSection(
    state: AuthenticationState,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        ) {
            CoreDividerLine()
            Text(
                text = "or continue with",
                style = CoreTypography.caption,
                color = CoreTheme.colors.textSecondary,
            )
            CoreDividerLine()
        }

        val appleButtonBackground = if (CoreTheme.isDarkTheme) Color.White else Color.Black
        val appleButtonContent = if (CoreTheme.isDarkTheme) Color.Black else Color.White

        SocialButton(
            text = "Sign in with Apple",
            background = appleButtonBackground,
            contentColor = appleButtonContent,
            onClick = {
                if (state.availableMethods.contains(NativeProvider.Apple))
                    state.eventSink(AuthenticationEvent.StartNativeAuth)
                else
                    state.eventSink(AuthenticationEvent.StartOAuth(OAuthProvider.Apple))
            },
            leadingContent = {
                Image(
                    painter = painterResource(Res.drawable.ic_apple_logo),
                    contentDescription = "Apple",
                    modifier = Modifier
                        .padding(CoreSpacing.xs)
                        .size(18.dp),
                    colorFilter = ColorFilter.tint(appleButtonContent),
                )
            },
        )

        SocialButton(
            text = "Sign in with Google",
            background = CoreTheme.colors.surface,
            contentColor = CoreTheme.colors.textPrimary,
            border = BorderStroke(1.dp, CoreTheme.colors.divider),
            onClick = {
                if (state.availableMethods.contains(NativeProvider.Google))
                    state.eventSink(AuthenticationEvent.StartNativeAuth)
                else
                    state.eventSink(AuthenticationEvent.StartOAuth(OAuthProvider.Google))
            },
            leadingContent = {
                Image(
                    painter = painterResource(Res.drawable.ic_google_logo),
                    contentDescription = "Google",
                    modifier = Modifier
                        .padding(CoreSpacing.xs)
                        .size(18.dp),
                )
            },
        )
    }
}

@Composable
private fun RowScope.CoreDividerLine() {
    Surface(
        modifier = Modifier
            .weight(1f)
            .height(1.dp),
        color = CoreTheme.colors.divider,
    ) {}
}

@Composable
private fun SocialButton(
    text: String,
    background: Color,
    contentColor: Color,
    onClick: () -> Unit,
    border: BorderStroke? = null,
    leadingContent: (@Composable () -> Unit)? = null,
) {
    Surface(
        onClick = onClick,
        shape = CoreShapes.md,
        color = background,
        border = border,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = CoreSpacing.sm, horizontal = CoreSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            leadingContent?.let {
                it()
                Spacer(modifier = Modifier.width(CoreSpacing.sm))
            }
            Text(
                text = text,
                style = CoreTypography.body,
                color = contentColor,
            )
        }
    }
}

@Composable
private fun Footnotes() {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        Icon(
            imageVector = Icons.Outlined.VerifiedUser,
            contentDescription = null,
            tint = CoreTheme.colors.onBackground,
        )
        Text(
            text = "Signing in only verifies your identity. Nothing is shared without your consent.",
            style = CoreTypography.footnote,
            color = CoreTheme.colors.textSecondary,
        )
    }
}

@Preview
@Composable
private fun PhoneIdentificationPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        AuthenticationUi(
            AuthenticationState(
                availableMethods = setOf(
                    OtpChannel.Phone,
                    OtpChannel.Email,
                    NativeProvider.Apple,
                    OAuthProvider.Apple,
                    NativeProvider.Google,
                    OAuthProvider.Google,
                ),
                canCancel = false,
                isLoading = false,
                otpErrorMessage = null,
                generalErrorMessage = null,
                selectedOtpChannel = OtpChannel.Phone,
                otpDestination = "",
                eventSink = {},
            )
        )
    }
}

@Preview
@Composable
private fun PhoneIdentificationFilledPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        AuthenticationUi(
            AuthenticationState(
                availableMethods = setOf(
                    OtpChannel.Phone,
                    OtpChannel.Email,
                    NativeProvider.Apple,
                    OAuthProvider.Apple,
                    NativeProvider.Google,
                    OAuthProvider.Google,
                ),
                canCancel = false,
                isLoading = false,
                otpErrorMessage = null,
                generalErrorMessage = null,
                selectedOtpChannel = OtpChannel.Phone,
                otpDestination = "312-555-1212",
                eventSink = {},
            )
        )
    }
}

@Preview
@Composable
private fun EmailIdentificationPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        AuthenticationUi(
            AuthenticationState(
                availableMethods = setOf(
                    OtpChannel.Phone,
                    OtpChannel.Email,
                    NativeProvider.Apple,
                    OAuthProvider.Apple,
                    NativeProvider.Google,
                    OAuthProvider.Google,
                ),
                canCancel = false,
                isLoading = false,
                otpErrorMessage = null,
                generalErrorMessage = null,
                selectedOtpChannel = OtpChannel.Email,
                otpDestination = "",
                eventSink = {},
            )
        )
    }
}

@Preview
@Composable
private fun EmailIdentificationFilledPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        AuthenticationUi(
            AuthenticationState(
                availableMethods = setOf(
                    OtpChannel.Phone,
                    OtpChannel.Email,
                    NativeProvider.Apple,
                    OAuthProvider.Apple,
                    NativeProvider.Google,
                    OAuthProvider.Google,
                ),
                canCancel = false,
                isLoading = false,
                otpErrorMessage = null,
                generalErrorMessage = null,
                selectedOtpChannel = OtpChannel.Email,
                otpDestination = "user@karawek.com",
                eventSink = {},
            )
        )
    }
}
